import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, FileText, ShieldCheck, Tag, X, ArrowRight, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

export default function SecuritiesBuyerDashboard() {
  const [email, setEmail] = useState("");
  const [tradelines, setTradelines] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [resell, setResell] = useState(null); // tradeline being resold
  const [price, setPrice] = useState("");
  const [resaleLink, setResaleLink] = useState("");
  const [params] = useSearchParams();

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setMsg("");
    try {
      const r = await axios.get(`${API}/securities/tradelines/by-email?email=${encodeURIComponent(email.trim())}`);
      setTradelines(r.data.tradelines || []);
      localStorage.setItem("stf_buyer_email", email.trim());
    } catch (err) { setMsg(errMsg(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const saved = localStorage.getItem("stf_buyer_email");
    if (saved) { setEmail(saved); }
    const sess = params.get("resale_session");
    if (sess) {
      axios.get(`${API}/securities/tradelines/resale/status?session_id=${sess}`)
        .then((r) => { if (r.data.paid && r.data.tradeline) setMsg(`✓ Purchase complete — tradeline ${r.data.tradeline.tradeline_no} is now yours. Look it up with the email you used.`); })
        .catch(() => {});
    }
    const rsess = params.get("renew_session");
    if (rsess) {
      axios.get(`${API}/securities/tradelines/renew/status?session_id=${rsess}`)
        .then((r) => { if (r.data.paid && r.data.tradeline) setMsg(`✓ Renewed — tradeline ${r.data.tradeline.tradeline_no} is valid for another 45 days.`); })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const saved = localStorage.getItem("stf_buyer_email");
    if (saved && tradelines === null) { setEmail(saved); }
  }, [tradelines]);

  const doResell = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/securities/tradelines/resell`, { verify_token: resell.verify_token, resale_price: parseFloat(price) });
      setResaleLink(data.resale_url);
    } catch (err) { alert(errMsg(err)); }
  };

  const cancelResale = async (tl) => {
    if (!window.confirm("Withdraw this resale listing?")) return;
    try { await axios.post(`${API}/securities/tradelines/cancel-resale`, { verify_token: tl.verify_token }); await lookup(); }
    catch (err) { alert(errMsg(err)); }
  };

  const renew = async (tl) => {
    try {
      const { data } = await axios.post(`${API}/securities/tradelines/renew`, { verify_token: tl.verify_token, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { alert(errMsg(err)); }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    try { return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000); } catch { return null; }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="buyer-dashboard-page">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#1C1C16]">
        <div>
          <div className="font-serif text-[20px]">SEUTRADE FINANCIAL SERVICES</div>
          <div className="text-[9.5px] tracking-[0.24em] uppercase text-[#C9A227] mt-1">My Tradelines · Buyer Portal</div>
        </div>
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.14em]">
          <Link to="/securities/marketplace" className="text-[#8A8A8A] hover:text-[#ECECE4]">Marketplace</Link>
          <Link to="/" className="text-[#8A8A8A] hover:text-[#ECECE4]">Home</Link>
        </div>
      </header>

      <main className="px-6 sm:px-10 py-10 max-w-4xl mx-auto">
        <h1 className="font-serif text-3xl sm:text-4xl">Your tradeline <span className="text-[#C9A227]">positions.</span></h1>
        <p className="text-[#9A9A90] text-sm mt-3 max-w-2xl">Enter the email you used at checkout to see every tradeline you own, re-download certificates, and resell positions.</p>

        <form onSubmit={lookup} className="mt-6 flex flex-wrap gap-3 items-center">
          <input required type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="buyer-email-input"
            className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none min-w-[280px]" />
          <button type="submit" data-testid="buyer-lookup-btn" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-5 py-2.5 rounded-md text-[12px] uppercase tracking-[0.12em] font-bold"><Search size={14} /> Find my positions</button>
        </form>

        {msg && <div className="mt-5 rounded-md border border-[#0B6B4F]/50 bg-[#0B6B4F]/10 text-[#3DBB8E] text-[13px] px-4 py-3" data-testid="buyer-msg">{msg}</div>}

        {loading ? <Loader2 className="animate-spin text-[#8A8A8A] mt-8" /> : tradelines === null ? null : tradelines.length === 0 ? (
          <p className="text-[#8A8A8A] text-sm mt-8" data-testid="buyer-empty">No tradelines found for that email.</p>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="buyer-tradelines">
            {tradelines.map((tl) => {
              const forResale = (tl.resale || {}).status === "for_resale";
              return (
                <div key={tl.id} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6" data-testid={`buyer-tl-${tl.id}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#C9A227]"><Tag size={13} /> {tl.instrument_type}</div>
                  <div className="font-mono text-[13px] text-[#E4C97A] mt-2">{tl.tradeline_no}</div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
                    <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Instrument face</div><div className="font-serif text-lg">{fmt(tl.face_amount)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">You paid</div><div className="font-serif text-lg text-[#3DBB8E]">{fmt(tl.price_paid)}</div></div>
                  </div>
                  <div className="text-[11px] text-[#8A8A8A] mt-2">Account {tl.account_no}{tl.duns_number ? ` · DUNS ${tl.duns_number}` : ""}</div>
                  {(() => { const dl = daysLeft(tl.expires_at); return tl.expires_at ? (
                    <div className={`mt-2 text-[11px] ${dl != null && dl <= 7 ? "text-[#FF8585]" : "text-[#9A9A90]"}`} data-testid={`buyer-tl-expiry-${tl.id}`}>
                      Valid through {String(tl.expires_at).slice(0, 10)}{dl != null ? ` · ${dl > 0 ? `${dl} day${dl === 1 ? "" : "s"} left` : "expired"}` : ""}
                      {(tl.auto_debit || {}).enrolled && <span className="text-[#3DBB8E]"> · auto-debit on</span>}
                    </div>
                  ) : null; })()}
                  {forResale && <div className="mt-2 text-[11px] text-[#E4C97A]" data-testid={`buyer-tl-resale-${tl.id}`}>Listed for resale at {fmt(tl.resale.price)}</div>}
                  <div className="mt-4 flex flex-wrap gap-4 items-center">
                    <a href={`${API}/securities/tradeline/certificate.pdf?t=${tl.verify_token}`} target="_blank" rel="noreferrer" data-testid={`buyer-cert-${tl.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><FileText size={13} /> Certificate</a>
                    <Link to={`/securities/verify/${tl.verify_token}`} data-testid={`buyer-verify-${tl.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><ShieldCheck size={13} /> Verify</Link>
                    <button onClick={() => renew(tl)} data-testid={`buyer-renew-${tl.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#3DBB8E] hover:text-[#7DD3B8]"><ArrowRight size={13} /> Renew 45d ({fmt(tl.renewal_fee)})</button>
                    {forResale ? (
                      <button onClick={() => cancelResale(tl)} data-testid={`buyer-cancel-resale-${tl.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#FF8585] hover:text-[#ffb3b3]"><X size={13} /> Withdraw resale</button>
                    ) : (
                      <button onClick={() => { setResell(tl); setPrice(""); setResaleLink(""); }} data-testid={`buyer-resell-${tl.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]"><ArrowRight size={13} /> Resell</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 border-t border-[#1C1C16] pt-6 text-[11px] text-[#6C6C64] leading-relaxed max-w-3xl" data-testid="buyer-disclaimer">
          <strong className="text-[#8A8A8A]">About your tradelines.</strong> These are <strong>corporate credit-enhancement instruments</strong>, each valid for 45 days and kept on-record by auto-debit of your account (you may also renew manually above). SEUTRADE FINANCIAL SERVICES is <strong>not a credit bureau</strong> and does not report to or electronically file with Dun &amp; Bradstreet, Experian Business, Equifax Business or any bureau. Your certificate, D&amp;B Business Trade Reference and UCC-1 are filing-ready records you (or a D&amp;B-approved Trade Exchange reporter) may submit; acceptance is at the receiving institution's discretion. Nothing here is an offer of securities, credit, or investment advice.
        </div>
      </main>

      {resell && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="resell-modal">
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-xl">Resell Tradeline</div>
              <button onClick={() => { setResell(null); setResaleLink(""); }} className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={18} /></button>
            </div>
            <p className="text-[13px] text-[#9A9A90] mb-4">{resell.instrument_type} · {resell.tradeline_no}</p>
            {resaleLink ? (
              <div data-testid="resale-link-block">
                <p className="text-[13px] text-[#3DBB8E] mb-2">✓ Listed for resale. Share this link with a buyer:</p>
                <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[12px] text-[#E4C97A] break-all font-mono" data-testid="resale-link">{resaleLink}</div>
                <button onClick={() => { navigator.clipboard?.writeText(resaleLink); }} className="mt-3 text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]" data-testid="resale-copy">Copy link</button>
                <button onClick={() => { setResell(null); setResaleLink(""); lookup(); }} className="mt-3 ml-4 text-[11px] uppercase tracking-[0.12em] text-[#8A8A8A] hover:text-[#ECECE4]">Done</button>
              </div>
            ) : (
              <form onSubmit={doResell} className="space-y-3">
                <label className="block text-[11px] uppercase tracking-[0.12em] text-[#8A8A8A]">Resale price (USD)</label>
                <input required type="number" min="1" step="0.01" placeholder="e.g. 8500" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="resell-price-input"
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                <button type="submit" data-testid="resell-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">List for resale <ArrowRight size={15} /></button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
